import { useEffect, useRef } from "react";
import { feature as topoFeature } from "topojson-client";
import styles from "./GlobalGlobe.module.css";

type Vec3 = [number, number, number];

export type GlobeRegionShare = {
  key: string;
  label: string;
  count: number;
  percent: number;
  percentLabel: string;
};

const PINS: Array<{ lat: number; lon: number }> = [
  { lat: 18.5, lon: 73.9 },
  { lat: 50.1, lon: 8.7 },
  { lat: 25.2, lon: 55.3 },
  { lat: -23.5, lon: -46.6 },
  { lat: 35.7, lon: 139.7 },
  { lat: 40.7, lon: -74.0 },
  { lat: 51.5, lon: -0.1 },
  { lat: -26.2, lon: 28.0 },
  { lat: -33.9, lon: 151.2 },
];

const LINKS: Array<[number, number]> = [
  [0, 1],
  [0, 2],
  [0, 4],
  [1, 5],
  [5, 3],
  [2, 7],
  [6, 5],
  [4, 8],
  [0, 8],
  [1, 7],
  [6, 2],
];

const REGION_COLORS: Record<string, string> = {
  Asia: "#04305c",
  Europe: "#3B82F6",
  Africa: "#22C55E",
  "North America": "#F59E0B",
  "South America": "#F97316",
  "Middle East": "#7C3AED",
  Oceania: "#14B8A6",
};

const LAND_STYLE = {
  green: { fill: "#5ea35a", stroke: "#3d7a42" },
  sand: { fill: "#d4b07a", stroke: "#a68452" },
  snow: { fill: "#f4f7fb", stroke: "#c5ced8" },
} as const;

type LandKind = keyof typeof LAND_STYLE;

/** Hot-desert belts. Gulf and Arabia stay sand; vegetated countries stay outside these boxes. */
function isAridLand(lat: number, lon: number) {
  if (lat >= 16 && lat <= 33 && lon >= -16 && lon <= 37) return true;
  if (lat >= 12 && lat <= 34 && lon >= 34 && lon <= 60) return true;
  if (lat >= 26 && lat <= 35 && lon >= 46 && lon <= 61) return true;
  if (lat >= 24 && lat <= 31 && lon >= 66 && lon <= 73) return true;
  if (lat <= -19 && lat >= -33 && lon >= 118 && lon <= 145) return true;
  if (lat <= -22 && lat >= -30 && lon >= 15 && lon <= 22) return true;
  return false;
}

function landKind(lat: number, lon: number): LandKind {
  if (lat >= 60 || lat <= -55) return "snow";
  if (isAridLand(lat, lon)) return "sand";
  return "green";
}

const TILT = 0.38;
const SPEED = 0.00022;
const D2R = Math.PI / 180;

type LandFeature = { rings: Vec3[][]; fill: string; stroke: string };
type ScreenPoint = { x: number; y: number; z: number; r2: number };

function vec(lat: number, lon: number): Vec3 {
  const p = lat * D2R;
  const l = lon * D2R;
  return [Math.cos(p) * Math.sin(l), Math.sin(p), Math.cos(p) * Math.cos(l)];
}

export default function GlobalGlobe({
  reducedMotion = false,
  regions = [],
}: {
  reducedMotion?: boolean;
  regions?: GlobeRegionShare[];
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const regionsRef = useRef(regions);
  regionsRef.current = regions;

  useEffect(() => {
    const root = wrapRef.current as HTMLDivElement | null;
    const surface = canvasRef.current as HTMLCanvasElement | null;
    if (!root || !surface) return undefined;
    const stage: HTMLDivElement = root;
    const board: HTMLCanvasElement = surface;
    const context = board.getContext("2d") as CanvasRenderingContext2D;

    const cosT = Math.cos(TILT);
    const sinT = Math.sin(TILT);
    let width = 0;
    let height = 0;
    let radius = 0;
    let cx = 0;
    let cy = 0;
    let legendWidth = 0;
    const legendGap = 22;
    let rot = 4;
    let last = performance.now();
    let speedMul = 1;
    let dragging = false;
    let hovering = false;
    let lastPointerX = 0;
    let frameId = 0;
    let cancelled = false;
    const landFeatures: LandFeature[] = [];

    const pins = PINS.map((pin) => ({ v: vec(pin.lat, pin.lon) }));

    const arcs = LINKS.map((pair, index) => {
      const a = pins[pair[0]].v;
      const b = pins[pair[1]].v;
      const dot = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
      const omega = Math.acos(dot);
      const sinOmega = Math.sin(omega) || 1;
      const steps = 48;
      const pts: Array<{ v: Vec3; lift: number }> = [];
      for (let step = 0; step <= steps; step += 1) {
        const t = step / steps;
        const k1 = Math.sin((1 - t) * omega) / sinOmega;
        const k2 = Math.sin(t * omega) / sinOmega;
        pts.push({
          v: [a[0] * k1 + b[0] * k2, a[1] * k1 + b[1] * k2, a[2] * k1 + b[2] * k2],
          lift: Math.sin(Math.PI * t) * (0.12 + omega * 0.11),
        });
      }
      return { pts, offset: index / LINKS.length };
    });

    function visibleRegions() {
      return regionsRef.current.filter((region) => region.count > 0 && region.percent > 0);
    }

    function project(v: Vec3, lift: number, cr: number, sr: number): ScreenPoint {
      const x = v[0] * cr + v[2] * sr;
      const z = v[2] * cr - v[0] * sr;
      const y = v[1];
      const y2 = y * cosT - z * sinT;
      const z2 = y * sinT + z * cosT;
      const k = 1 + (lift || 0);
      return {
        x: cx + radius * k * x,
        y: cy - radius * k * y2,
        z: z2 * k,
        r2: (x * x + y2 * y2) * k * k,
      };
    }

    function ringToVec3(ring: unknown): Vec3[] {
      if (!Array.isArray(ring)) return [];
      return ring.map((point) => {
        const pair = point as [number, number];
        return vec(pair[1], pair[0]);
      });
    }

    function ringCentroid(ring: unknown): { lat: number; lon: number } | null {
      if (!Array.isArray(ring)) return null;
      let lat = 0;
      let lon = 0;
      let count = 0;
      ring.forEach((point) => {
        if (!Array.isArray(point) || point.length < 2) return;
        lon += Number(point[0]);
        lat += Number(point[1]);
        count += 1;
      });
      if (!count) return null;
      return { lat: lat / count, lon: lon / count };
    }

    function pushGeometry(geom: { type: string; coordinates: unknown } | null | undefined) {
      if (!geom) return;
      const polys =
        geom.type === "Polygon"
          ? [geom.coordinates]
          : geom.type === "MultiPolygon"
            ? (geom.coordinates as unknown[])
            : [];
      polys.forEach((poly) => {
        if (!Array.isArray(poly) || !poly.length) return;
        const centroid = ringCentroid(poly[0]);
        const style = LAND_STYLE[centroid ? landKind(centroid.lat, centroid.lon) : "green"];
        const rings = poly.map(ringToVec3).filter((ring) => ring.length > 0);
        if (rings.length) landFeatures.push({ rings, fill: style.fill, stroke: style.stroke });
      });
    }

    function featureAvgZ(item: LandFeature, cr: number, sr: number) {
      let sum = 0;
      let count = 0;
      item.rings.forEach((ring) => {
        ring.forEach((point) => {
          sum += project(point, 0, cr, sr).z;
          count += 1;
        });
      });
      return count ? sum / count : -1;
    }

    function drawLand(cr: number, sr: number) {
      if (!landFeatures.length) return;
      context.save();
      context.beginPath();
      context.arc(cx, cy, radius, 0, Math.PI * 2);
      context.clip();
      const sorted = landFeatures
        .slice()
        .sort((a, b) => featureAvgZ(a, cr, sr) - featureAvgZ(b, cr, sr));
      sorted.forEach((item) => {
        const avgZ = featureAvgZ(item, cr, sr);
        if (avgZ < -0.05) return;
        item.rings.forEach((ring) => {
          if (ring.length < 3) return;
          context.beginPath();
          ring.forEach((point, index) => {
            const projected = project(point, 0, cr, sr);
            if (index === 0) context.moveTo(projected.x, projected.y);
            else context.lineTo(projected.x, projected.y);
          });
          context.closePath();
          context.globalAlpha = 1;
          context.fillStyle = item.fill;
          context.fill();
          context.strokeStyle = item.stroke;
          context.lineWidth = 0.6;
          context.stroke();
        });
      });
      context.globalAlpha = 1;
      context.restore();
    }

    function drawLegend(rows: GlobeRegionShare[]) {
      if (!rows.length || legendWidth < 72) return;
      const fontSize = Math.max(11, Math.min(12.5, height / 14));
      const rowH = fontSize + 8;
      const blockH = rows.length * rowH;
      const top = Math.max(4, (height - blockH) / 2);
      const left = Math.min(cx + radius + legendGap, width - legendWidth);
      context.textBaseline = "middle";
      rows.forEach((row, index) => {
        const y = top + index * rowH + rowH / 2;
        context.beginPath();
        context.fillStyle = REGION_COLORS[row.key] ?? "#94A3B8";
        context.arc(left + 5, y, 4, 0, Math.PI * 2);
        context.fill();
        context.font = `600 ${fontSize}px Sora, sans-serif`;
        context.fillStyle = "#334155";
        context.textAlign = "left";
        const nameMax = legendWidth - 46;
        let name = row.label;
        if (context.measureText(name).width > nameMax) {
          while (name.length > 1 && context.measureText(`${name}…`).width > nameMax) {
            name = name.slice(0, -1);
          }
          name = `${name}…`;
        }
        context.fillText(name, left + 13, y);
        context.font = `700 ${fontSize}px Sora, sans-serif`;
        context.fillStyle = "#04305c";
        context.textAlign = "right";
        context.fillText(row.percentLabel || `${row.percent}%`, Math.min(left + legendWidth - 2, width - 8), y);
      });
    }

    function placeGlobe() {
      const rows = visibleRegions();
      const showLegend = rows.length > 0 && width > 168;
      legendWidth = showLegend ? Math.min(164, Math.max(148, width * 0.42)) : 0;
      const gap = showLegend ? legendGap : 0;
      const leftPad = 2;
      const room = Math.max(1, width - leftPad - gap - legendWidth);
      radius = Math.min(room / 2, height * 0.48);
      cx = leftPad + radius;
      cy = height / 2;
    }

    function resize() {
      const bounds = stage.getBoundingClientRect();
      const nextWidth = Math.max(1, bounds.width);
      const nextHeight = Math.max(1, bounds.height);
      if (nextWidth === width && nextHeight === height && board.width > 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = nextWidth;
      height = nextHeight;
      board.width = width * dpr;
      board.height = height * dpr;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function frame(now: number) {
      if (cancelled) return;
      resize();
      placeGlobe();
      const dt = Math.min(64, now - last);
      last = now;
      if (!reducedMotion && !dragging) rot += dt * SPEED * speedMul;
      const cr = Math.cos(rot);
      const sr = Math.sin(rot);
      context.clearRect(0, 0, width, height);

      const ocean = context.createRadialGradient(cx - radius * 0.28, cy - radius * 0.32, radius * 0.08, cx, cy, radius);
      ocean.addColorStop(0, "#7ec8f2");
      ocean.addColorStop(0.55, "#2f86d4");
      ocean.addColorStop(1, "#1a6aaf");
      context.fillStyle = ocean;
      context.beginPath();
      context.arc(cx, cy, radius, 0, Math.PI * 2);
      context.fill();
      context.lineWidth = 1.2;
      context.strokeStyle = "rgba(120, 190, 235, 0.9)";
      context.stroke();

      drawLand(cr, sr);

      arcs.forEach((arc) => {
        let prev: { x: number; y: number; vis: boolean } | null = null;
        context.lineWidth = 1.2;
        arc.pts.forEach((point) => {
          const projected = project(point.v, point.lift, cr, sr);
          const vis = projected.z > 0 || projected.r2 > 1;
          if (prev && vis && prev.vis) {
            const alpha = 0.18 + 0.35 * Math.min(1, Math.max(0, projected.z + 0.4));
            context.strokeStyle = `rgba(150,140,255,${alpha})`;
            context.beginPath();
            context.moveTo(prev.x, prev.y);
            context.lineTo(projected.x, projected.y);
            context.stroke();
          }
          prev = { x: projected.x, y: projected.y, vis };
        });
        const travel = ((now * 0.00018 + arc.offset) % 1) * (arc.pts.length - 1);
        const head = arc.pts[Math.floor(travel)];
        const headPoint = project(head.v, head.lift, cr, sr);
        if (headPoint.z > 0 || headPoint.r2 > 1) {
          context.fillStyle = "rgba(255,214,90,.95)";
          context.shadowColor = "rgba(255,195,0,.9)";
          context.shadowBlur = 8;
          context.beginPath();
          context.arc(headPoint.x, headPoint.y, 2.2, 0, Math.PI * 2);
          context.fill();
          context.shadowBlur = 0;
        }
      });

      pins.forEach((pin, index) => {
        const point = project(pin.v, 0, cr, sr);
        if (point.z <= 0.02) return;
        const alpha = Math.min(1, point.z * 2.2);
        const pulse = (now / 1400 + index * 0.37) % 1;
        context.strokeStyle = `rgba(255,195,0,${0.5 * (1 - pulse) * alpha})`;
        context.lineWidth = 1.5;
        context.beginPath();
        context.arc(point.x, point.y, 3 + pulse * 11, 0, Math.PI * 2);
        context.stroke();
        context.fillStyle = `rgba(255,195,0,${alpha})`;
        context.beginPath();
        context.arc(point.x, point.y, 3.2, 0, Math.PI * 2);
        context.fill();
      });

      drawLegend(visibleRegions());
      frameId = window.requestAnimationFrame(frame);
    }

    const applySpeed = () => {
      speedMul = dragging ? 0 : hovering ? 0.15 : 1;
    };
    const onEnter = () => {
      hovering = true;
      applySpeed();
    };
    const onLeave = () => {
      hovering = false;
      applySpeed();
    };
    const onPointerDown = (event: PointerEvent) => {
      dragging = true;
      lastPointerX = event.clientX;
      applySpeed();
      stage.setPointerCapture(event.pointerId);
    };
    const onPointerMove = (event: PointerEvent) => {
      if (!dragging) return;
      rot += (event.clientX - lastPointerX) * 0.008;
      lastPointerX = event.clientX;
    };
    const onPointerUp = () => {
      dragging = false;
      applySpeed();
    };

    stage.addEventListener("mouseenter", onEnter);
    stage.addEventListener("mouseleave", onLeave);
    stage.addEventListener("pointerdown", onPointerDown);
    stage.addEventListener("pointermove", onPointerMove);
    stage.addEventListener("pointerup", onPointerUp);
    stage.addEventListener("pointercancel", onPointerUp);
    window.addEventListener("resize", resize);
    resize();
    frameId = window.requestAnimationFrame(frame);

    const loadCountries = fetch("https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json")
      .then((response) => response.json())
      .then((topo: { objects: { countries: object } }) => {
        if (cancelled) return;
        const countries = topoFeature(topo, topo.objects.countries);
        landFeatures.length = 0;
        (countries.features || []).forEach((item) => {
          pushGeometry(item.geometry);
        });
      })
      .catch(() => undefined);

    void loadCountries;

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      stage.removeEventListener("mouseenter", onEnter);
      stage.removeEventListener("mouseleave", onLeave);
      stage.removeEventListener("pointerdown", onPointerDown);
      stage.removeEventListener("pointermove", onPointerMove);
      stage.removeEventListener("pointerup", onPointerUp);
      stage.removeEventListener("pointercancel", onPointerUp);
    };
  }, [reducedMotion]);

  return (
    <section className={`${styles.section} ${styles.embedded}`} aria-label="Global company distribution">
      <div className={styles.globe} ref={wrapRef}>
        <canvas ref={canvasRef} className={styles.canvas} />
      </div>
    </section>
  );
}
